import { Grid2, Stack } from '@mui/material';
import SidebarMenu from 'components/SidebarMenu/SidebarMenu';
import { isNil } from 'lodash';
import React, { ReactNode, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import LayoutContainer from './LayoutContainer';

const BASE_PADDING = {
	xs: 2,
	md: 3,
	lg: 4
};

export const CONTENT_PADDING_BOTTOM = {
	xs: 3,
	md: 5
};

interface IPageLayoutProps {
	children: ReactNode;
	sidebarContent?: ReactNode;
}

const PageLayout: React.FC<IPageLayoutProps> = ({
	children,
	sidebarContent = null
}) => {
	const layoutContentRef = useRef<HTMLDivElement>(null);

	const hasSidebar = !isNil(sidebarContent);

	const pageContentCombinedWidth = hasSidebar ? 1800 : 1400;

	const location = useLocation();

	useEffect(() => {
		/* istanbul ignore if */
		if (!layoutContentRef.current) {
			return;
		}

		layoutContentRef.current.scrollTop = 0;
	}, [location]);

	const sidebarSizes = {
		xs: 4,
		xl: 3
	};

	const mainContentSizes = Object.fromEntries(
		Object.entries(sidebarSizes).map(([k, v]) => [k, 12 - v])
	);

	return (
		<Stack
			p={BASE_PADDING}
			flexGrow={1}
			width="100%"
			overflow="hidden"
			alignContent="center"
			maxWidth={pageContentCombinedWidth}
		>
			<Grid2 container flexGrow={1} spacing={4} overflow="hidden">
				{hasSidebar && (
					<Grid2
						data-testid="layout-sidebar"
						direction="column"
						overflow="hidden"
						container
						size={sidebarSizes}
						height="100%"
					>
						<SidebarMenu>{sidebarContent}</SidebarMenu>
					</Grid2>
				)}
				<Grid2
					direction="column"
					container
					size={hasSidebar ? mainContentSizes : 12}
					height="100%"
				>
					<LayoutContainer
						data-testid="layout-main"
						ref={layoutContentRef}
						sx={{
							maxWidth: '100%',
							borderRadius: 5,
							overflowY: 'auto',
							position: 'relative',
							pt: {
								xs: 3,
								md: 5
							},
							pb: CONTENT_PADDING_BOTTOM,
							flexGrow: 1,
							px: {
								xs: 3,
								md: 6
							}
						}}
					>
						{children}
					</LayoutContainer>
				</Grid2>
			</Grid2>
		</Stack>
	);
};

export default PageLayout;
